"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.replayPlaceGivenDefect = replayPlaceGivenDefect;
exports.replayDefectGivenPlace = replayDefectGivenPlace;
exports.replayPhotoAi = replayPhotoAi;
const place_stats_compute_1 = require("./place-stats-compute");
const place_stats_rank_1 = require("./place-stats-rank");
const place_stats_1 = require("./place-stats");
function hitRate(ranks, k) {
    if (!ranks.length)
        return 0;
    return ranks.filter((rank) => rank >= 0 && rank < k).length / ranks.length;
}
function foldReports(reports, folds) {
    const buckets = Array.from({ length: folds }, () => []);
    reports.forEach((report, index) => {
        buckets[index % folds].push(report);
    });
    return buckets;
}
function mentionEvents(report) {
    const events = [];
    const located = report.blocks.filter((block) => block.places.length);
    for (let index = 0; index < located.length; index += 1) {
        const block = located[index];
        if (!block.defectId)
            continue;
        const previousPlaceId = located[index - 1]?.places[0]?.id;
        const nextPlaceId = located[index + 1]?.places[0]?.id;
        for (const place of block.places) {
            events.push({ defectId: block.defectId, placeId: place.id, previousPlaceId, nextPlaceId });
        }
    }
    return events;
}
function rankFor(stats, catalogPlaces, event) {
    const ranked = (0, place_stats_rank_1.rankPlaceChips)({
        catalogPlaces,
        previousPlaceId: event.previousPlaceId,
        selectedDefectId: event.defectId,
        stats,
        limit: catalogPlaces.length,
    });
    return ranked.findIndex((place) => place.id === event.placeId);
}
function currentChipRank(previousPlaceId, nextPlaceId, placeId) {
    const seen = new Set();
    const order = [];
    const push = (id) => {
        if (!id || seen.has(id))
            return;
        seen.add(id);
        order.push(id);
    };
    push(previousPlaceId);
    if (nextPlaceId !== previousPlaceId)
        push(nextPlaceId);
    return (0, place_stats_1.rankOfId)(order, placeId);
}
function trainDefects(reports) {
    const counts = new Map();
    for (const report of reports) {
        for (const block of report.blocks) {
            if (!block.defectId)
                continue;
            const current = counts.get(block.defectId) ?? { n: 0, categoryId: block.categoryId };
            current.n += 1;
            current.categoryId = current.categoryId ?? block.categoryId;
            counts.set(block.defectId, current);
        }
    }
    return [...counts.entries()]
        .map(([id, value]) => ({ id, categoryId: value.categoryId, score: value.n }))
        .sort((left, right) => (right.score ?? 0) - (left.score ?? 0));
}
function replayPlaceGivenDefect(input) {
    const folds = input.folds ?? 5;
    const buckets = foldReports(input.reports, folds);
    const rows = [];
    const companyId = input.companyId ?? 'replay';
    for (let fold = 0; fold < folds; fold += 1) {
        const test = buckets[fold];
        const train = buckets.flatMap((bucket, index) => (index === fold ? [] : bucket));
        if (!train.length || !test.length)
            continue;
        const stats = (0, place_stats_compute_1.computePlaceSuggestionStats)({
            companyId,
            reports: train,
            windowStart: 'train',
            windowEnd: 'train',
            computedAt: 'replay',
        });
        for (const report of test) {
            for (const event of mentionEvents(report)) {
                rows.push({
                    reportId: report.id,
                    defectId: event.defectId,
                    placeId: event.placeId,
                    previousPlaceId: event.previousPlaceId,
                    rank: rankFor(stats, input.catalogPlaces, event),
                    baselineRank: rankFor(stats, input.catalogPlaces, {
                        previousPlaceId: event.previousPlaceId,
                        placeId: event.placeId,
                    }),
                    currentRank: currentChipRank(event.previousPlaceId, event.nextPlaceId, event.placeId),
                });
            }
        }
    }
    const ranks = rows.map((row) => row.rank);
    const baseline = rows.map((row) => row.baselineRank);
    const current = rows.map((row) => row.currentRank);
    return {
        nPredictions: rows.length,
        top1: hitRate(ranks, 1),
        top3: hitRate(ranks, 3),
        baselineTop1: hitRate(baseline, 1),
        baselineTop3: hitRate(baseline, 3),
        currentTop1: hitRate(current, 1),
        currentTop3: hitRate(current, 3),
        currentBaseline: 'neighbor_above_below',
        currentCoversAi: false,
        rows: input.keepRows === false ? [] : rows,
    };
}
function replayDefectGivenPlace(input) {
    const folds = input.folds ?? 5;
    const buckets = foldReports(input.reports, folds);
    const none = [];
    const predicted = [];
    const tapped = [];
    for (let fold = 0; fold < folds; fold += 1) {
        const test = buckets[fold];
        const train = buckets.flatMap((bucket, index) => (index === fold ? [] : bucket));
        if (!train.length || !test.length)
            continue;
        const stats = (0, place_stats_compute_1.computePlaceSuggestionStats)({
            companyId: input.companyId ?? 'replay',
            reports: train,
            windowStart: 'train',
            windowEnd: 'train',
            computedAt: 'replay',
        });
        const defects = trainDefects(train);
        for (const report of test) {
            for (const event of mentionEvents(report)) {
                none.push((0, place_stats_1.rankOfId)(defects.map((defect) => defect.id), event.defectId));
                const predictedPlaceId = (0, place_stats_rank_1.predictNextPlaceId)({
                    previousPlaceId: event.previousPlaceId,
                    catalogPlaces: input.catalogPlaces,
                    stats,
                });
                predicted.push((0, place_stats_1.rankOfId)((0, place_stats_rank_1.softReweightDefects)({
                    defects,
                    stats,
                    tappedPlaceIds: predictedPlaceId ? [predictedPlaceId] : [],
                }).map((defect) => defect.id), event.defectId));
                tapped.push((0, place_stats_1.rankOfId)((0, place_stats_rank_1.softReweightDefects)({ defects, stats, tappedPlaceIds: [event.placeId] }).map((defect) => defect.id), event.defectId));
            }
        }
    }
    return {
        nPredictions: none.length,
        noneTop5: hitRate(none, 5),
        predictedTop5: hitRate(predicted, 5),
        tappedTop5: hitRate(tapped, 5),
    };
}
function realCatalogPlaces(catalog) {
    return catalog.filter((place) => place.id && !/^x\d+$/.test(place.id));
}
function commonCatalogPlaces(stats, catalog) {
    const real = realCatalogPlaces(catalog);
    const byId = new Map(real.map((place) => [place.id, place]));
    const ordered = [];
    for (const share of stats.globalPlacePrior) {
        const place = byId.get(share.placeId);
        if (place)
            ordered.push(place);
    }
    const seen = new Set(ordered.map((place) => place.id));
    for (const place of real) {
        if (!seen.has(place.id))
            ordered.push(place);
    }
    return ordered;
}
function photoAiPlaces(scenario, event, common) {
    const truePlace = common.find((place) => place.id === event.placeId);
    const wrong = common.filter((place) => place.id !== event.placeId);
    if (scenario === 'true_first') {
        return truePlace ? [truePlace, ...wrong.slice(0, 4)] : wrong.slice(0, 5);
    }
    if (scenario === 'wrong_common')
        return wrong.slice(0, 5);
    const head = wrong[0];
    const rest = wrong.slice(1, 4);
    return [head, truePlace, ...rest].filter((place) => Boolean(place)).slice(0, 5);
}
function scorePhotoAiScenario(scenario, reports, catalogPlaces, stats, common) {
    const ranks = [];
    let historySum = 0;
    let historyAtLeast2 = 0;
    for (const report of reports) {
        for (const event of mentionEvents(report)) {
            const aiPlaces = scenario === 'none' ? [] : photoAiPlaces(scenario, event, common);
            const aiIds = new Set(aiPlaces.map((place) => place.id));
            const ranked = (0, place_stats_rank_1.rankPlaceChips)({
                catalogPlaces,
                previousPlaceId: event.previousPlaceId,
                selectedDefectId: event.defectId,
                stats,
                aiPlaces,
                limit: place_stats_1.PLACE_CHIP_LIMIT,
            });
            ranks.push(ranked.findIndex((place) => place.id === event.placeId));
            const history = ranked.filter((place) => place.id === event.previousPlaceId || !aiIds.has(place.id)).length;
            historySum += history;
            if (history >= 2)
                historyAtLeast2 += 1;
        }
    }
    const nRows = ranks.length;
    return {
        scenario,
        nRows,
        top1: hitRate(ranks, 1),
        top3: hitRate(ranks, 3),
        meanHistoryChips: nRows ? historySum / nRows : 0,
        historyAtLeast2: nRows ? historyAtLeast2 / nRows : 0,
    };
}
/** Photo-flow replay using real catalog rooms as AI suggestions. */
function replayPhotoAi(input) {
    const stats = (0, place_stats_compute_1.computePlaceSuggestionStats)({
        companyId: input.companyId ?? 'replay',
        reports: input.reports,
        windowStart: 'train',
        windowEnd: 'train',
        computedAt: 'replay',
    });
    const common = commonCatalogPlaces(stats, input.catalogPlaces);
    const baseline = scorePhotoAiScenario('none', input.reports, input.catalogPlaces, stats, common);
    return {
        baseline,
        scenarios: ['true_first', 'wrong_common', 'mixed'].map((scenario) => (scorePhotoAiScenario(scenario, input.reports, input.catalogPlaces, stats, common))),
    };
}
